import React, { useState, useEffect, useRef } from 'react';
import { fundAPI } from '../../services/api';
import { Download, Trash2, FileText, Loader2, CheckCircle, AlertCircle, RefreshCw, Search, Calendar, Mail, Shield, Upload, Lock, X } from 'lucide-react';
import SOAEmailTab from './SOAEmailTab';
import { useAuth } from '../../contexts/AuthContext';

const SOAGeneration = ({ canEdit }) => {
  const { user: currentUser } = useAuth();
  const [funds, setFunds] = useState([]);
  const [selectedFundId, setSelectedFundId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Generation options
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [fromDate, setFromDate] = useState('since_inception');
  const [toDate, setToDate] = useState('');
  const [includeNav, setIncludeNav] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const pollRef = useRef(null);

  // Document management
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState('generate');

  // PDF password protection (mirror Drawdown Notice flow)
  const [passwordMode, setPasswordMode] = useState('none');
  const [passwordField, setPasswordField] = useState('pan');
  const [passwordExpression, setPasswordExpression] = useState('lower(first(account_name,4)) + ddmm(dob)');
  const [passwordCsvFile, setPasswordCsvFile] = useState(null);
  const [passwordCsvBase64, setPasswordCsvBase64] = useState('');

  const handlePasswordCsvUpload = (file) => {
    if (!file) { setPasswordCsvFile(null); setPasswordCsvBase64(''); return; }
    setPasswordCsvFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',', 2)[1] : result;
      setPasswordCsvBase64(base64);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    loadFunds();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const loadFunds = async () => {
    try {
      const res = await fundAPI.getAll();
      setFunds(res.data);
    } catch (err) { console.error('Failed to load funds:', err); }
    finally { setLoading(false); }
  };

  const handleFundChange = async (fundId) => {
    setSelectedFundId(fundId);
    setSelectedTemplate('');
    setSelectedAccounts(new Set());
    setSelectAll(false);
    setTemplates([]);
    setAccounts([]);
    setDocuments([]);
    setJobStatus(null);
    if (!fundId) return;
    try {
      const [tmplRes, accRes, docRes] = await Promise.all([
        fundAPI.getSOATemplates(fundId),
        fundAPI.getAccountsForSOA(fundId),
        fundAPI.getSOADocuments(fundId),
      ]);
      setTemplates(tmplRes.data);
      setAccounts(accRes.data);
      setDocuments(docRes.data);
    } catch (err) { console.error('Failed to load fund SOA data:', err); }
  };

  const handleSelectAll = () => {
    if (selectAll) setSelectedAccounts(new Set());
    else setSelectedAccounts(new Set(accounts.map(a => a.id)));
    setSelectAll(!selectAll);
  };

  const toggleAccount = (id) => {
    setSelectedAccounts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleGenerate = async () => {
    if (!selectedTemplate) return alert('Please select a template');
    if (selectedAccounts.size === 0) return alert('Please select at least one investor account');
    if (passwordMode === 'csv' && !passwordCsvBase64) return alert('Please upload a Password CSV before generating.');
    setGenerating(true);
    setJobStatus(null);
    try {
      const payload = {
        template_id: selectedTemplate,
        account_ids: Array.from(selectedAccounts),
        from_date: fromDate === 'since_inception' ? 'since_inception' : fromDate || null,
        to_date: toDate || null,
        include_nav: includeNav,
      };
      // Attach password protection fields when enabled
      if (passwordMode && passwordMode !== 'none') {
        payload.password_mode = passwordMode;
        if (passwordMode === 'single_field') payload.password_field = passwordField;
        else if (passwordMode === 'combination') payload.password_expression = passwordExpression;
        else if (passwordMode === 'csv') {
          payload.password_csv_b64 = passwordCsvBase64;
          payload.password_csv_filename = passwordCsvFile?.name || 'passwords.csv';
        }
      }
      const res = await fundAPI.generateBulkSOA(selectedFundId, payload);
      const jobId = res.data.job_id;
      setJobStatus({ status: 'processing', total: res.data.total, completed: 0, failed: 0, job_id: jobId });
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await fundAPI.getSOAGenerationStatus(selectedFundId, jobId);
          // Preserve `job_id` locally so the Abort button always has a
          // handle even if the backend response omits it.
          setJobStatus({ ...statusRes.data, job_id: statusRes.data.id || statusRes.data.job_id || jobId });
          if (['completed', 'partial', 'failed', 'aborted'].includes(statusRes.data.status)) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setGenerating(false);
            const docRes = await fundAPI.getSOADocuments(selectedFundId);
            setDocuments(docRes.data);
          }
        } catch (e) { console.error('Poll error:', e); }
      }, 2000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start generation');
      setGenerating(false);
    }
  };

  const handleDownloadDoc = async (docId, accountName) => {
    try {
      const res = await fundAPI.downloadSOADocument(docId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      // Prefer the server-supplied filename. CORS must expose Content-Disposition
      // for axios to surface it; without that header, fall back to a safe default.
      const disposition = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || '';
      // RFC 5987 first (filename*=UTF-8''…), then the plain `filename="…"` form.
      let filename = '';
      const m87 = disposition.match(/filename\*\s*=\s*[^']+''([^;]+)/i);
      if (m87) {
        try { filename = decodeURIComponent(m87[1]); } catch { filename = m87[1]; }
      }
      if (!filename) {
        const m = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
        if (m) filename = m[1].trim();
      }
      link.download = filename || `SOA_${accountName}.pdf`.replace(/\s/g, '_');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { alert('Download failed'); }
  };

  const handleBulkDownload = async () => {
    const ids = selectedDocs.size > 0 ? Array.from(selectedDocs) : [];
    try {
      const res = await fundAPI.bulkDownloadSOA(selectedFundId, ids);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'SOA_Statements.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      const skipped = parseInt(res.headers?.['x-soa-skipped'] || '0', 10);
      if (skipped > 0) {
        alert(`Downloaded, but ${skipped} SOA file(s) could not be retrieved from storage and were skipped.`);
      }
    } catch (err) {
      let msg = 'Bulk download failed';
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) msg = `Bulk download failed: ${parsed.detail}`;
        } else if (err.response?.data?.detail) {
          msg = `Bulk download failed: ${err.response.data.detail}`;
        } else if (err.response?.status) {
          msg = `Bulk download failed (HTTP ${err.response.status})`;
        } else if (err.message) {
          msg = `Bulk download failed: ${err.message}`;
        }
      } catch { /* keep default msg */ }
      alert(msg);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this SOA?')) return;
    try {
      await fundAPI.deleteSOADocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      setSelectedDocs(prev => { const n = new Set(prev); n.delete(docId); return n; });
    } catch (err) { alert('Failed to delete'); }
  };

  const handleBulkDelete = async () => {
    if (selectedDocs.size === 0) return;
    if (!window.confirm(`Delete ${selectedDocs.size} SOA document(s)?`)) return;
    try {
      await fundAPI.bulkDeleteSOA(selectedFundId, Array.from(selectedDocs));
      setDocuments(prev => prev.filter(d => !selectedDocs.has(d.id)));
      setSelectedDocs(new Set());
    } catch (err) { alert('Bulk delete failed'); }
  };

  const filteredDocs = documents.filter(d =>
    !searchTerm || d.account_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="p-6 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="soa-generation">
      {/* View tabs */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button onClick={() => setView('generate')} className={`px-4 py-1.5 text-sm rounded-md transition-colors ${view === 'generate' ? 'bg-white shadow text-blue-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`} data-testid="soa-generate-tab">Generate SOA</button>
        <button onClick={() => setView('documents')} className={`px-4 py-1.5 text-sm rounded-md transition-colors ${view === 'documents' ? 'bg-white shadow text-blue-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`} data-testid="soa-documents-tab">Generated SOAs ({documents.length})</button>
        <button onClick={() => setView('email')} className={`px-4 py-1.5 text-sm rounded-md transition-colors flex items-center ${view === 'email' ? 'bg-white shadow text-blue-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`} data-testid="soa-email-tab"><Mail size={14} className="mr-1.5" />Email to Investors</button>
      </div>

      {/* Generate View */}
      {view === 'generate' && (
        <div className="space-y-4">
          {/* Step 1: Fund Selection */}
          <div className="bg-white border rounded-lg p-4">
            <label className="text-sm font-medium text-gray-700 block mb-2">1. Select Fund</label>
            <select value={selectedFundId} onChange={(e) => handleFundChange(e.target.value)} className="w-full max-w-md px-3 py-2 border rounded-lg text-sm" data-testid="soa-fund-select">
              <option value="">Choose a fund...</option>
              {funds.map(f => <option key={f.id} value={f.id}>{f.name} ({f.fund_currency || 'INR'})</option>)}
            </select>
          </div>

          {selectedFundId && (
            <>
              {/* Step 2: Template + Options */}
              <div className="bg-white border rounded-lg p-4 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">2. Select SOA Template</label>
                  {templates.length === 0 ? (
                    <p className="text-sm text-amber-600">No SOA templates found. Create one in Fund Details &gt; Document Templates tab first.</p>
                  ) : (
                    <select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)} className="w-full max-w-md px-3 py-2 border rounded-lg text-sm" data-testid="soa-template-select">
                      <option value="">Choose a template...</option>
                      {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  )}
                </div>

                {/* Date range + NAV toggle */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t">
                  <div>
                    <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1"><Calendar size={12} /> From Date</label>
                    <select value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="soa-from-date-select">
                      <option value="since_inception">Since Inception</option>
                      <option value="_custom">Custom Date</option>
                    </select>
                    {fromDate === '_custom' && (
                      <input type="date" onChange={(e) => setFromDate(e.target.value)} className="w-full mt-1 px-2 py-1.5 border rounded text-sm" data-testid="soa-from-date-input" />
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-1"><Calendar size={12} /> To Date</label>
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" data-testid="soa-to-date-input" placeholder="Leave empty for today" />
                    <p className="text-xs text-gray-400 mt-0.5">Leave empty for current date</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">NAV in SOA</label>
                    <div className="flex items-center gap-3 mt-1">
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input type="radio" name="includeNav" checked={includeNav} onChange={() => setIncludeNav(true)} className="text-blue-600" data-testid="soa-nav-yes" />
                        With NAV
                      </label>
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <input type="radio" name="includeNav" checked={!includeNav} onChange={() => setIncludeNav(false)} className="text-blue-600" data-testid="soa-nav-no" />
                        Without NAV
                      </label>
                    </div>
                  </div>
                </div>

                {/* PDF Password Protection (mirrors Drawdown Notice flow) */}
                <div className="border-t pt-3 mt-1">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-2">
                    <Shield size={14} className="text-amber-600" /> PDF Password Protection
                  </label>
                  <select
                    value={passwordMode}
                    onChange={e => setPasswordMode(e.target.value)}
                    className="w-full max-w-md text-sm border rounded-lg px-2.5 py-2"
                    data-testid="soa-password-mode-select"
                  >
                    <option value="none">None — Generate unprotected PDFs</option>
                    <option value="single_field">Single Field (e.g. PAN, DOB)</option>
                    <option value="combination">Combination DSL (e.g. lower(first(account_name,4))+ddmm(dob))</option>
                    <option value="csv">CSV Upload (Investor Ref &rarr; Password)</option>
                  </select>

                  {passwordMode === 'single_field' && (
                    <div className="mt-2 max-w-md" data-testid="soa-password-single-field-block">
                      <label className="text-xs text-gray-500 block mb-1">Password Field</label>
                      <select
                        value={passwordField}
                        onChange={e => setPasswordField(e.target.value)}
                        className="w-full text-sm border rounded-lg px-2.5 py-2"
                        data-testid="soa-password-field-select"
                      >
                        <option value="pan">PAN</option>
                        <option value="dob">Date of Birth (DDMM)</option>
                        <option value="dob_full">Date of Birth (DDMMYYYY)</option>
                        <option value="folio_no">Folio No.</option>
                        <option value="ppm_no">PPM No.</option>
                        <option value="mobile">Mobile (last 4 digits)</option>
                      </select>
                    </div>
                  )}

                  {passwordMode === 'combination' && (
                    <div className="mt-2 max-w-2xl" data-testid="soa-password-combination-block">
                      <label className="text-xs text-gray-500 block mb-1">DSL Expression</label>
                      <input
                        type="text"
                        value={passwordExpression}
                        onChange={e => setPasswordExpression(e.target.value)}
                        placeholder="lower(first(account_name,4)) + ddmm(dob)"
                        className="w-full text-sm border rounded-lg px-2.5 py-2 font-mono"
                        data-testid="soa-password-expression-input"
                      />
                      <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                        <strong>Fields:</strong> <code>name</code>, <code>account_name</code>, <code>pan</code>, <code>dob</code>, <code>folio_no</code>, <code>ppm_no</code>, <code>mobile</code><br />
                        <strong>Functions:</strong> <code>lower(x)</code>, <code>upper(x)</code>, <code>first(x,n)</code>, <code>last(x,n)</code>, <code>digits(x)</code>, <code>ddmm(dob)</code>, <code>ddmmyyyy(dob)</code>, <code>yyyy(dob)</code><br />
                        <strong>Operator:</strong> <code>+</code> concatenates. Quoted literals supported, e.g. <code>&quot;FUND-&quot; + upper(pan)</code>
                      </p>
                    </div>
                  )}

                  {passwordMode === 'csv' && (
                    <div className="mt-2 max-w-2xl" data-testid="soa-password-csv-block">
                      <label className="text-xs text-gray-500 block mb-1">Upload CSV / XLSX</label>
                      <input
                        type="file"
                        accept=".csv,.xlsx"
                        onChange={e => handlePasswordCsvUpload(e.target.files?.[0] || null)}
                        className="w-full text-sm"
                        data-testid="soa-password-csv-input"
                      />
                      {passwordCsvFile && (
                        <p className="text-xs text-green-700 mt-1">
                          <Upload size={11} className="inline mr-1" /> {passwordCsvFile.name} ({Math.round(passwordCsvFile.size / 1024)} KB) ready to upload
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                        Columns: any of <code>account_id</code>, <code>account_name</code>, <code>pan</code>, <code>folio_no</code>, <code>ppm_no</code> + a <code>password</code> column. Match is case-insensitive.
                      </p>
                    </div>
                  )}

                  {passwordMode !== 'none' && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2 max-w-2xl">
                      Protected PDFs will be saved alongside the originals. Investors with missing fields are saved without encryption (warning shown after completion). Email send will attach the protected PDF.
                    </p>
                  )}
                </div>
              </div>

              {/* Step 3: Account Selection */}
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">3. Select Investor Accounts ({selectedAccounts.size} of {accounts.length})</label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-500">
                    <input type="checkbox" checked={selectAll} onChange={handleSelectAll} className="rounded border-gray-300" data-testid="soa-select-all" />
                    Select All
                  </label>
                </div>
                {accounts.length === 0 ? (
                  <p className="text-sm text-gray-400">No investor accounts found with investments in this fund.</p>
                ) : (
                  <div className="max-h-56 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="w-10 p-2"></th>
                          <th className="text-left p-2 font-medium text-gray-600">Account Name</th>
                          <th className="text-left p-2 font-medium text-gray-600">PAN</th>
                          <th className="text-left p-2 font-medium text-gray-600">Folio No.</th>
                          <th className="text-left p-2 font-medium text-gray-600">Classes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accounts.map(acc => (
                          <tr key={acc.id} className="border-t hover:bg-gray-50">
                            <td className="p-2 text-center">
                              <input type="checkbox" checked={selectedAccounts.has(acc.id)} onChange={() => toggleAccount(acc.id)} className="rounded border-gray-300" data-testid={`soa-account-check-${acc.id}`} />
                            </td>
                            <td className="p-2 font-medium">{acc.account_name}</td>
                            <td className="p-2 text-gray-500">{acc.pan || '-'}</td>
                            <td className="p-2 text-gray-500">{acc.folio_no || '-'}</td>
                            <td className="p-2">{(acc.class_of_units || []).map(c => <span key={c} className="inline-block px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded mr-1">{c}</span>)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Generate Button */}
              <button onClick={handleGenerate} disabled={generating || !selectedTemplate || selectedAccounts.size === 0} className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="soa-generate-btn">
                {generating ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                {generating ? 'Generating...' : `Generate SOA (${selectedAccounts.size} accounts)`}
              </button>

              {/* Progress */}
              {jobStatus && (
                <div className="bg-white border rounded-lg p-4" data-testid="soa-job-status">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{jobStatus.status === 'processing' ? 'Generating...' : jobStatus.status === 'completed' ? 'Complete' : jobStatus.status === 'partial' ? 'Completed with errors' : jobStatus.status === 'aborted' ? 'Aborted' : 'Failed'}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">{jobStatus.completed}/{jobStatus.total} completed{jobStatus.failed > 0 && `, ${jobStatus.failed} failed`}</span>
                      {/* Abort button — user-triggered stop for a stuck or
                          undesired batch. Flips `aborted=true` on the job
                          doc; the background loop exits at its next
                          iteration and marks the job `aborted`. */}
                      {jobStatus.status === 'processing' && (
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm('Abort this SOA generation? Completed SOAs stay saved; pending accounts stop.')) return;
                            try {
                              await fundAPI.abortSOAGeneration(selectedFundId, jobStatus.job_id || jobStatus.id);
                            } catch (err) {
                              alert(err.response?.data?.detail || 'Abort failed');
                            }
                          }}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50"
                          data-testid="soa-abort-btn"
                        >
                          <X size={12} /> Abort
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="flex h-2.5 rounded-full overflow-hidden">
                      <div className="bg-blue-600 transition-all duration-300" style={{ width: `${(jobStatus.completed / jobStatus.total) * 100}%` }} />
                      {jobStatus.failed > 0 && <div className="bg-red-500 transition-all duration-300" style={{ width: `${(jobStatus.failed / jobStatus.total) * 100}%` }} />}
                    </div>
                  </div>
                  {jobStatus.status === 'completed' && <div className="flex items-center gap-1.5 mt-2 text-green-600 text-sm"><CheckCircle size={16} /> All SOAs generated successfully</div>}
                  {jobStatus.status === 'aborted' && <div className="flex items-center gap-1.5 mt-2 text-amber-700 text-sm"><AlertCircle size={16} /> Generation aborted by user. {jobStatus.completed} SOAs were saved.</div>}
                  {jobStatus.failed_accounts?.length > 0 && (
                    <div className="mt-2 text-sm">
                      <span className="text-red-600 font-medium">Failed:</span>
                      <ul className="mt-1 space-y-0.5">{jobStatus.failed_accounts.map((fa, idx) => <li key={idx} className="text-red-500 text-xs flex items-center gap-1"><AlertCircle size={12} /> {fa.account_name || fa.account_id}</li>)}</ul>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Documents View */}
      {view === 'documents' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="relative w-64">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by account name..." className="w-full pl-8 pr-3 py-1.5 border rounded-lg text-sm" data-testid="soa-doc-search" />
            </div>
            <div className="flex items-center gap-2">
              {selectedDocs.size > 0 && canEdit && (
                <button onClick={handleBulkDelete} className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 text-sm rounded-lg hover:bg-red-100" data-testid="soa-bulk-delete-btn">
                  <Trash2 size={14} /> Delete ({selectedDocs.size})
                </button>
              )}
              {documents.length > 0 && selectedFundId && (
                <button onClick={handleBulkDownload} className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 text-sm rounded-lg hover:bg-blue-100" data-testid="soa-bulk-download-btn">
                  <Download size={14} /> Download {selectedDocs.size > 0 ? `(${selectedDocs.size})` : 'All'}
                </button>
              )}
              <button onClick={() => selectedFundId && handleFundChange(selectedFundId)} className="p-1.5 text-gray-400 hover:text-gray-600" title="Refresh"><RefreshCw size={16} /></button>
            </div>
          </div>
          {!selectedFundId && <p className="text-center py-8 text-gray-400 text-sm">Select a fund first to view generated SOAs.</p>}
          {selectedFundId && filteredDocs.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">{documents.length === 0 ? 'No SOA documents generated yet.' : 'No documents match your search.'}</div>
          ) : selectedFundId && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-10 p-2"><input type="checkbox" checked={selectedDocs.size === filteredDocs.length && filteredDocs.length > 0} onChange={() => { if (selectedDocs.size === filteredDocs.length) setSelectedDocs(new Set()); else setSelectedDocs(new Set(filteredDocs.map(d => d.id))); }} className="rounded border-gray-300" /></th>
                    <th className="text-left p-2 font-medium text-gray-600">Account Name</th>
                    <th className="text-left p-2 font-medium text-gray-600">Statement Date</th>
                    <th className="text-left p-2 font-medium text-gray-600">Generated On</th>
                    <th className="text-left p-2 font-medium text-gray-600">Protection</th>
                    <th className="text-right p-2 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocs.map(doc => (
                    <tr key={doc.id} className="border-t hover:bg-gray-50">
                      <td className="p-2 text-center"><input type="checkbox" checked={selectedDocs.has(doc.id)} onChange={() => setSelectedDocs(prev => { const n = new Set(prev); if (n.has(doc.id)) n.delete(doc.id); else n.add(doc.id); return n; })} className="rounded border-gray-300" /></td>
                      <td className="p-2 font-medium">{doc.account_name}</td>
                      <td className="p-2 text-gray-500">{doc.statement_date}</td>
                      <td className="p-2 text-gray-500">{new Date(doc.created_at).toLocaleDateString()}</td>
                      <td className="p-2">
                        {doc.is_encrypted ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full font-medium" title={`Mode: ${doc.password_mode || ''}${doc.password ? ` · Password: ${doc.password}` : ''}`} data-testid={`soa-encrypted-${doc.id}`}>
                            <Lock size={11} /> Encrypted
                          </span>
                        ) : doc.encryption_skipped_reason ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-orange-700 text-xs rounded-full font-medium" title={doc.encryption_skipped_reason} data-testid={`soa-encrypt-skipped-${doc.id}`}>
                            <AlertCircle size={11} /> Skipped
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleDownloadDoc(doc.id, doc.account_name)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Download" data-testid={`soa-download-${doc.id}`}><Download size={14} /></button>
                          {canEdit && <button onClick={() => handleDeleteDoc(doc.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Delete" data-testid={`soa-delete-${doc.id}`}><Trash2 size={14} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Email to Investors view */}
      {view === 'email' && (
        <div className="space-y-4">
          {/* Fund selector — re-uses the same state so switching back to Generate keeps the selection */}
          <div className="bg-white border rounded-lg p-4">
            <label className="text-sm font-medium text-gray-700 block mb-2">Fund</label>
            <select value={selectedFundId} onChange={(e) => handleFundChange(e.target.value)} className="w-full max-w-md px-3 py-2 border rounded-lg text-sm" data-testid="soa-email-fund-select">
              <option value="">Choose a fund...</option>
              {funds.map(f => <option key={f.id} value={f.id}>{f.name} ({f.fund_currency || 'INR'})</option>)}
            </select>
          </div>
          <SOAEmailTab fundId={selectedFundId} currentUser={currentUser} />
        </div>
      )}
    </div>
  );
};

export default SOAGeneration;
